/**
 * Configuration Preloader
 *
 * Preloads all configuration data on app initialization to warm up cache.
 * This ensures subsequent screens load instantly from cache.
 */

import { configurationService } from '../api/configuration';
import { createLogger } from '../config/environment';

const logger = createLogger('ConfigPreloader');

export class ConfigurationPreloader {
  private isPreloaded = false;
  private preloadPromise: Promise<void> | null = null;

  /**
   * Preload all configuration data in parallel
   * This should be called during app initialization
   */
  async preloadAll(): Promise<void> {
    // If already preloading or preloaded, return existing promise
    if (this.preloadPromise) {
      return this.preloadPromise;
    }

    if (this.isPreloaded) {
      logger.info('✅ Configuration already preloaded');
      return Promise.resolve();
    }

    logger.info('🔄 Starting configuration preload...');
    const startTime = Date.now();

    this.preloadPromise = this.doPreload();

    try {
      await this.preloadPromise;
      this.isPreloaded = true;
      const duration = Date.now() - startTime;
      logger.info(`✅ Configuration preload complete in ${duration}ms`);
    } catch (error) {
      logger.error('❌ Configuration preload failed', error);
      // Don't throw - let individual screens handle errors
    }

    return this.preloadPromise;
  }

  /**
   * Internal preload implementation
   */
  private async doPreload(): Promise<void> {
    try {
      // Preload all configuration data in parallel
      await Promise.all([
        this.preloadCountries(),
        this.preloadShopTypes(),
        this.preloadUOMs(),
        this.preloadCategories(),
      ]);
    } catch (error) {
      logger.error('Error during configuration preload', error);
      throw error;
    }
  }

  /**
   * Preload countries
   */
  private async preloadCountries(): Promise<void> {
    try {
      logger.debug('📡 Preloading countries...');
      await configurationService.getCountries({ activeOnly: true });
      logger.debug('✅ Countries preloaded');
    } catch (error) {
      logger.warn('Failed to preload countries', error);
    }
  }

  /**
   * Preload shop types
   */
  private async preloadShopTypes(): Promise<void> {
    try {
      logger.debug('📡 Preloading shop types...');
      await configurationService.getShopTypes({ activeOnly: true });
      logger.debug('✅ Shop types preloaded');
    } catch (error) {
      logger.warn('Failed to preload shop types', error);
    }
  }

  /**
   * Preload UOMs
   */
  private async preloadUOMs(): Promise<void> {
    try {
      logger.debug('📡 Preloading UOMs...');
      await configurationService.getUnitOfMeasurements({ activeOnly: true });
      logger.debug('✅ UOMs preloaded');
    } catch (error) {
      logger.warn('Failed to preload UOMs', error);
    }
  }

  /**
   * Preload categories for common shop types
   */
  private async preloadCategories(): Promise<void> {
    try {
      logger.debug('📡 Preloading categories...');
      // Preload all categories (not shop-type specific)
      // Individual shop types will be loaded on-demand and cached
      await configurationService.getCategories({ activeOnly: true });
      logger.debug('✅ Categories preloaded');
    } catch (error) {
      logger.warn('Failed to preload categories', error);
    }
  }

  /**
   * Reset preload state (for testing or re-initialization)
   */
  reset(): void {
    this.isPreloaded = false;
    this.preloadPromise = null;
    logger.info('🔄 Configuration preloader reset');
  }

  /**
   * Check if configuration is preloaded
   */
  isReady(): boolean {
    return this.isPreloaded;
  }
}

// Singleton instance
export const configPreloader = new ConfigurationPreloader();

